import { useCallback, useState, useEffect } from 'react';

import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { createContainer, useContainer } from 'unstated-next';

import { GET_HOME_PAGE_DATA, GET_ERRORS } from '../../../../../config/apiPaths';
import { getUserUuidHeaders } from '../../../../../utils/httpUtils';
import { getUrlParams, getUrls, processApiPath } from '../../../../../utils/url';
import AdminAuthContainer from '../../../AdminAuthContainer';

interface HomeContainerState {
    homeData?: Record<string, any>; // result of the GET_CLASS_DATA_PATH api call
}

type LoadingStatus = 'INITIAL' | 'LOADING' | 'SUCCESS' | 'ERROR';

const useHomeContainer = (state: HomeContainerState = {}) => {
    console.log('HomeContainer');
    const { api } = getUrls();

    const [homeData, setHomeData] = useState<any>();
    const [insightsDetailErrors, setInsightsDetailErrors] = useState<any>([]);

    const { t: tCommon } = useTranslation('common');

    const { uuid: userUuid, authMode, showDemoContent } = useContainer(AdminAuthContainer);

    const [getHomeStatus, setGetHomeStatus] = useState<LoadingStatus>('INITIAL');
    const [errorMessage, setErrorMessage] = useState<string>('');

    const { homeId: homeIdV1 } = useParams<{ homeId?: string }>();
    const { home_id: homeIdV2 } = getUrlParams();
    const homeId = homeIdV1 || homeIdV2;

    const returnErrorMessage = useCallback(
        (error: any): string => {
            console.error(error);
            return tCommon('errorMessage');
        },
        [tCommon]
    );

    const getHomeDataCall = useCallback(async () => {
        const { data } = await axios.get(
            processApiPath(GET_HOME_PAGE_DATA, undefined, {
                auth_mode: authMode,
                demo_content: String(showDemoContent)
            }),
            { headers: getUserUuidHeaders(userUuid) }
        );

        if (data?.success === false) {
            throw Error(data?.error);
        }
        return data;
    }, [authMode, showDemoContent, userUuid]);

    const getErrorsCall = useCallback(async () => {
        const { data } = await axios.get(
            processApiPath(GET_ERRORS, undefined, {
                auth_mode: authMode,
                demo_content: String(showDemoContent),
                time_span: 'week'
            }),
            { headers: getUserUuidHeaders(userUuid) }
        );
        setInsightsDetailErrors(data);
    }, [authMode, showDemoContent, userUuid]);

    const getHome = useCallback(async () => {
        setGetHomeStatus('LOADING');

        try {
            const data = state.homeData ?? (await getHomeDataCall());

            setHomeData(data);
            setGetHomeStatus('SUCCESS');
        } catch (err) {
            const errorMsg = returnErrorMessage(err);
            setErrorMessage(errorMsg);
            setGetHomeStatus('ERROR');
        }
    }, [state.homeData, getHomeDataCall, returnErrorMessage]);

    useEffect(() => {
        getHome();
    }, [getHome]);

    useEffect(() => {
        getErrorsCall();
    }, [getErrorsCall]);

    return {
        homeId,
        api,
        homeData,
        getHomeStatus,
        errorMessage,
        insightsDetailErrors
    };
};

const HomeContainer = createContainer(useHomeContainer);

export default HomeContainer;
